import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MoreHorizontal, Plus, GripVertical, Archive, Edit2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/shared/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { listApi, cardApi } from '../services/eztaskApi';
import KanbanCard from './KanbanCard';

export default function KanbanList({ list, onCardClick, onRefresh, filteredCards }) {const { t } = useTranslation("eztask");
  const [showAddCard, setShowAddCard] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(list.name);

  // Use filtered cards if provided, otherwise fall back to list.cards
  const displayCards = filteredCards !== undefined ? filteredCards : list.cards || [];

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: list.list_id,
    data: { type: 'list', list }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };

  const handleAddCard = async () => {
    if (!newCardTitle.trim()) return;
    try {
      await cardApi.create(list.list_id, { title: newCardTitle });
      setNewCardTitle('');
      setShowAddCard(false);
      onRefresh();
      toast.success('Card created');
    } catch {
      toast.error('Failed to create card');
    }
  };

  const handleRename = async () => {
    if (!editName.trim() || editName === list.name) {
      setIsEditing(false);
      setEditName(list.name);
      return;
    }
    try {
      await listApi.update(list.list_id, { name: editName });
      onRefresh();
      setIsEditing(false);
      toast.success('List renamed');
    } catch {
      toast.error('Failed to rename list');
    }
  };

  const handleArchive = async () => {
    try {
      await listApi.archive(list.list_id);
      onRefresh();
      toast.success('List archived');
    } catch {
      toast.error('Failed to archive list');
    }
  };

  const cardCount = displayCards?.length || 0;
  const totalCards = list.cards?.length || 0;
  const wipWarning = list.wip_limit && totalCards >= list.wip_limit;
  const isFiltered = filteredCards !== undefined && filteredCards.length !== totalCards;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="w-72 flex-shrink-0 bg-slate-800/90 backdrop-blur-sm rounded-lg flex flex-col max-h-[calc(100vh-160px)]"
      data-testid={`kanban-list-${list.list_id}`}>
      
      {/* List Header */}
      <div className="p-3 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <button {...attributes} {...listeners} className="cursor-grab text-slate-500 hover:text-slate-300">
            <GripVertical className="w-4 h-4" />
          </button>
          {isEditing ?
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => e.key === 'Enter' && handleRename()}
            className="h-7 bg-slate-900 border-slate-600 text-sm"
            autoFocus /> :


          <h3
            className="font-semibold text-white truncate cursor-pointer hover:text-blue-400"
            onClick={() => setIsEditing(true)}>
            
              {list.name}
            </h3>
          }
          <span className={`text-xs px-2 py-0.5 rounded ${wipWarning ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-400'}`}>
            {isFiltered ? `${cardCount}/${totalCards}` : cardCount}
            {list.wip_limit && ` (max ${list.wip_limit})`}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="text-slate-400 hover:text-white">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-slate-800 border-slate-700">
            <DropdownMenuItem onClick={() => setIsEditing(true)} className="text-slate-300 hover:bg-slate-700">
              <Edit2 className="w-4 h-4 mr-2" />{t("rename", "Rename")}

            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleArchive} className="text-red-400 hover:bg-slate-700">
              <Archive className="w-4 h-4 mr-2" />{t("archiveList", "Archive List")}

            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Cards Container */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2">
        <SortableContext items={displayCards?.map((c) => c.card_id) || []} strategy={verticalListSortingStrategy}>
          {displayCards?.map((card) =>
          <KanbanCard
            key={card.card_id}
            card={card}
            listId={list.list_id}
            onClick={() => onCardClick(card)} />

          )}
        </SortableContext>

        {/* Add Card */}
        {showAddCard ?
        <div className="bg-slate-900 rounded-lg p-2">
            <Input
            value={newCardTitle}
            onChange={(e) => setNewCardTitle(e.target.value)}
            placeholder="Enter card title..."
            className="mb-2 bg-slate-800 border-slate-600 text-sm"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && handleAddCard()}
            data-testid="new-card-input" />
          
            <div className="flex gap-2">
              <Button onClick={handleAddCard} size="sm" data-testid="add-card-confirm">{t("add", "Add")}</Button>
              <Button variant="ghost" size="sm" onClick={() => {setShowAddCard(false);setNewCardTitle('');}}>{t("cancel", "Cancel")}

            </Button>
            </div>
          </div> :

        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700"
          onClick={() => setShowAddCard(true)}
          disabled={wipWarning}
          data-testid="add-card-btn">
          
            <Plus className="w-4 h-4 mr-2" />{t("addACard", "Add a card")}

        </Button>
        }
      </div>
    </div>);

}